import {
  useState,
  useEffect,
  createContext,
  useContext,
  ReactNode,
} from "react";
import { authAPI, apiClient } from "@/api";
import { accessRequest, Client } from "@/api/client-access";
import { User, LoginRequest, APIError } from "@/types";

interface AuthContextType {
  user: User | null;
  clients: Client[];
  activeClient: string;
  accessEnabled: boolean;
  switchClient: (id: string) => void;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (credentials: LoginRequest) => Promise<void>;
  logout: () => void;
  refreshToken: () => Promise<void>;
  error: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

export function useAuthContext() {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [clients, setClients] = useState<Client[]>([]);
  const [accessEnabled, setAccessEnabled] = useState(false);
  const activeClient =
    sessionStorage.getItem("loop_client") || user?.client_id || "omaha";
  const switchClient = (id: string) => {
    if (!clients.some((c) => c.id === id) || id === activeClient) return;
    sessionStorage.setItem("loop_client", id);
    // A full navigation destroys query caches, media blobs and old package URLs.
    window.location.assign("/loop");
  };
  async function loadAccess(account: User) {
    if (account.must_change_password) {
      setAccessEnabled(true);
      setClients([]);
      sessionStorage.setItem("loop_client", account.client_id || "omaha");
      return;
    }
    const result = await accessRequest("/api/access/me");
    setClients(result.clients);
    setAccessEnabled(result.enabled);
    const previous = sessionStorage.getItem("loop_client");
    if (
      !account.is_superuser ||
      !result.clients.some((c: Client) => c.id === previous)
    ) {
      sessionStorage.setItem("loop_client", account.client_id || "omaha");
    }
  }

  const isAuthenticated = !!user && !!token;

  const login = async (credentials: LoginRequest) => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await authAPI.login(credentials);
      const { access_token, user: userData } = response.data;

      // Store token in localStorage and set in API client
      localStorage.setItem("auth_token", access_token);
      localStorage.setItem("refresh_token", response.data.refresh_token);
      localStorage.setItem("user", JSON.stringify(userData));
      apiClient.setToken(access_token);
      await loadAccess(userData);
      setToken(access_token);
      setUser(userData);
    } catch (err) {
      setUser(null);
      setToken(null);
      localStorage.removeItem("auth_token");
      localStorage.removeItem("refresh_token");
      localStorage.removeItem("user");
      apiClient.clearToken();
      const error = err as APIError;
      setError(error.message || "Login failed");
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    const logoutToken = localStorage.getItem("auth_token");
    setClients([]);
    setAccessEnabled(false);
    sessionStorage.removeItem("loop_client");
    setUser(null);
    setToken(null);
    setError(null);

    // Clear localStorage and API client token
    localStorage.removeItem("auth_token");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("user");
    apiClient.clearToken();
    try {
      await fetch(
        (import.meta.env.VITE_API_BASE_URL || "") + "/api/auth/logout",
        {
          method: "POST",
          headers: { Authorization: `Bearer ${logoutToken || ""}` },
          signal: AbortSignal.timeout(5000),
        },
      );
    } catch {
      /* Local sign-out still works offline. */
    }
  };

  const refreshToken = async () => {
    try {
      const storedRefreshToken = localStorage.getItem("refresh_token");
      if (!storedRefreshToken)
        throw new Error("Please sign in again to refresh this session");
      const response = await authAPI.refreshToken(storedRefreshToken);
      const { access_token, user: userData } = response.data;

      setToken(access_token);
      setUser(userData);

      localStorage.setItem("auth_token", access_token);
      localStorage.setItem("refresh_token", response.data.refresh_token);
      localStorage.setItem("user", JSON.stringify(userData));
      apiClient.setToken(access_token);
      await loadAccess(userData);
    } catch (err) {
      console.error("Token refresh failed:", err);
      logout();
    }
  };

  // Initialize auth state from localStorage
  useEffect(() => {
    const initAuth = async () => {
      try {
        const storedToken = localStorage.getItem("auth_token");
        const storedUser = localStorage.getItem("user");

        if (storedToken && storedUser) {
          setToken(storedToken);
          setUser(JSON.parse(storedUser));
          apiClient.setToken(storedToken);

          // Verify token is still valid
          try {
            const response = await authAPI.getCurrentUser();
            await loadAccess(response.data);
            setUser(response.data);
          } catch (err) {
            console.error("Token validation failed:", err);
            logout();
          }
        }
      } catch (err) {
        console.error("Auth initialization failed:", err);
        logout();
      } finally {
        setIsLoading(false);
      }
    };

    initAuth();
  }, []);

  // Set up token refresh interval
  useEffect(() => {
    if (!isAuthenticated) return;

    const interval = setInterval(
      () => {
        refreshToken().catch(console.error);
      },
      15 * 60 * 1000,
    ); // Refresh every 15 minutes

    return () => clearInterval(interval);
  }, [isAuthenticated]);

  // Listen for auth events
  useEffect(() => {
    const handleAuthUnauthorized = () => {
      setError("Your session has expired. Please log in again.");
      logout();
    };

    const handleAuthForbidden = () => {
      setError("You do not have permission to perform this action.");
    };

    window.addEventListener("auth:unauthorized", handleAuthUnauthorized);
    window.addEventListener("auth:forbidden", handleAuthForbidden);

    return () => {
      window.removeEventListener("auth:unauthorized", handleAuthUnauthorized);
      window.removeEventListener("auth:forbidden", handleAuthForbidden);
    };
  }, []);

  return {
    user,
    clients,
    activeClient,
    accessEnabled,
    switchClient,
    token,
    isAuthenticated,
    isLoading,
    login,
    logout,
    refreshToken,
    error,
  };
}

export { AuthContext };
